import cx from "classnames";
import React from "react";

import ProductCoverImage from "@/mk2/apps/bazaar/components/ProductCoverImage";
import { BazaarMessageType, ProductEntity, ReviewEntity } from "@/mk2/apps/bazaar/schemas";
import { bazaarSellerUrl } from "@/mk2/apps/bazaar/urls";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import { ImgVarWidth } from "@/mk2/components/ImgVarWidth";
import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { OverlayLoadable } from "@/mk2/components/OverlayLoadable";
import { Tooltip } from "@/mk2/components/Tooltip";
import { timeAt } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import useOverlay from "@/mk2/hooks/useOverlay";
import { PhotoEntity, UserEntity } from "@/mk2/schemas";
import { BZ2_NEW_MESSAGE } from "@/mk/autogenerated/translation";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./Message.mscss";

export enum Type {
    SENT = "sent",
    RECEIVED = "received",
}

interface OwnProps {
    className?: string;
    author: UserEntity;
    sentDate: string;
    message: React.ReactNode;
    messageType: BazaarMessageType;
    type: Type;
    isNew?: boolean;
    photos?: PhotoEntity[];
    product?: ProductEntity;
    showProduct: boolean;
    review?: ReviewEntity;
}

type Props = OwnProps;

export const Message: React.FunctionComponent<Props> = React.memo(
    ({
        className,
        author,
        isNew = false,
        sentDate,
        photos,
        message,
        messageType,
        product,
        showProduct,
        type,
        review,
    }) => {
        const [
            showTooltip,
            onProductMouseEnter,
            onProductMouseLeave,
            productRef,
            getProduct,
            bodyContainer,
        ] = useOverlay<HTMLDivElement>();

        return (
            <div className={cx(styles.Message, className)}>
                <div className={styles.Message__message}>
                    {type === Type.RECEIVED && (
                        <>
                            <Link to={url(bazaarSellerUrl, { username: author.username })}>
                                <AvatarImg
                                    className={styles.Message__avatar}
                                    user={author}
                                    size={32}
                                />
                            </Link>
                            <div
                                className={cx(
                                    styles.Message__triangle,
                                    isNew && styles["Message__triangle--new"],
                                )}
                            />
                        </>
                    )}
                    <div
                        className={cx(
                            styles.Message__bubble,
                            styles[`Message__bubble--${type}`],
                            isNew && styles["Message__bubble--new"],
                        )}
                    >
                        <div className={styles.Message__content}>
                            <div
                                className={cx(
                                    styles.Message__content__text,
                                    (messageType === BazaarMessageType.SystemReservedFromBuyer ||
                                        !!review) &&
                                        styles["Message__content__text--isSystem"],
                                )}
                            >
                                {type === Type.RECEIVED && (
                                    <>
                                        <Link
                                            className={styles.Message__content__text__username}
                                            to={url(bazaarSellerUrl, { username: author.username })}
                                        >
                                            {author.username}
                                        </Link>{" "}
                                    </>
                                )}
                                <span className={styles.Message__content__text__text}>
                                    {message}
                                </span>
                            </div>
                            <div className={styles.Message__desktopDate}>
                                {isNew && (
                                    <span className={styles.Message__desktopDate__newMessage}>
                                        {BZ2_NEW_MESSAGE}
                                    </span>
                                )}
                                <span>{timeAt(sentDate)}</span>
                            </div>
                            {showProduct && product && (
                                <div
                                    className={styles.Message__content__product}
                                    ref={productRef}
                                    onMouseEnter={onProductMouseEnter}
                                    onMouseLeave={onProductMouseLeave}
                                >
                                    <ProductCoverImage product={product} size={26} />
                                    <OverlayLoadable
                                        placement="top"
                                        show={showTooltip}
                                        container={bodyContainer}
                                        target={getProduct}
                                    >
                                        {({ props, arrowProps, placement }) => (
                                            <Tooltip
                                                placement={placement}
                                                containerRef={props.ref}
                                                style={props.style}
                                                arrowRef={arrowProps.ref}
                                                arrowStyle={arrowProps.style}
                                            >
                                                {product.title}
                                            </Tooltip>
                                        )}
                                    </OverlayLoadable>
                                </div>
                            )}
                        </div>
                        {photos && photos.length > 0 && (
                            <div className={styles.Message__photos}>
                                {photos.map((photo) => (
                                    <a
                                        data-google-interstitial="false"
                                        className={styles.Message__photos__photo}
                                        key={photo.id}
                                        target="_blank"
                                        href={photoUrl(photo, "s1600x1600")}
                                    >
                                        <ImgVarWidth
                                            photo={photo}
                                            photoVersion="snx224"
                                            height={112}
                                        />
                                    </a>
                                ))}
                            </div>
                        )}
                        <div className={styles.Message__mobileDate}>
                            {isNew && (
                                <span className={styles.Message__mobileDate__newMessage}>
                                    {BZ2_NEW_MESSAGE}
                                </span>
                            )}
                            <span>{timeAt(sentDate)}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

Message.displayName = "Message";

interface PlaceholderOwnProps {
    className?: string;
    type: Type;
}

export const MessagePlaceholder: React.FunctionComponent<PlaceholderOwnProps> = React.memo(
    ({ className, type }) => {
        return (
            <div className={cx(styles.Message, className)}>
                <div className={styles.Message__message}>
                    {type === Type.RECEIVED && (
                        <>
                            <LoadingPlaceholder width={32} height={32} />
                            <div className={styles.Message__triangle} />
                        </>
                    )}
                    <div className={cx(styles.Message__bubble, styles[`Message__bubble--${type}`])}>
                        <div className={styles.Message__content}>
                            <div className={styles.Message__content__text}>
                                {type === Type.RECEIVED && (
                                    <>
                                        <LoadingPlaceholder width={80} />{" "}
                                    </>
                                )}
                                <span className={styles.Message__content__text__text}>
                                    <LoadingPlaceholder width="75%" />
                                </span>
                            </div>
                            <div className={styles.Message__content__date}>
                                <span>
                                    <LoadingPlaceholder width={65} />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

MessagePlaceholder.displayName = "MessagePlaceholder";
